import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Github, Twitter, Linkedin, Heart, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageContext";

const BTC_ADDRESS = "17tjXCLHDFZcESHE5VKHPezwcFR6vA1KxJ";

export function Footer() {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const footerLinks = {
    product: [
      { label: t.footer.findJobs, href: "/jobs" },
      { label: t.footer.cvBuilder, href: "/cv-builder" },
      { label: t.footer.aiDiagnostics, href: "/diagnostics" },
      { label: t.footer.pricing, href: "/pricing" },
    ],
    resources: [
      { label: t.footer.careerTips, href: "/career-tips" },
      { label: t.footer.interviewGuide, href: "/interview-guide" },
      { label: t.footer.cvTemplates, href: "/cv-templates" },
      { label: t.footer.blog, href: "/blog" },
    ],
    company: [
      { label: t.footer.aboutUs, href: "/about" },
      { label: t.footer.supportDeveloper, href: "/support" },
      { label: t.footer.privacyPolicy, href: "/privacy" },
      { label: t.footer.termsOfService, href: "/terms" },
    ],
  };

  const copyBtc = async () => {
    try {
      await navigator.clipboard.writeText(BTC_ADDRESS);
      setCopied(true);
      toast({ title: t.footer.copied, description: "BTC address copied" });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({ title: "Failed", variant: "destructive" });
    }
  };

  return (
    <footer className="border-t border-border bg-card">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-ai flex items-center justify-center">
                <Zap className="w-5 h-5 text-accent-foreground" />
              </div>
              <span className="font-display font-bold text-xl text-foreground">
                Remote<span className="text-gradient-ai">AI</span>
              </span>
            </Link>
            <p className="text-muted-foreground text-sm mb-6 max-w-xs">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="GitHub">
                <Github className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-4 text-base">{t.footer.product}</h3>
            <ul className="space-y-2">
              {footerLinks.product.map((link) => (
                <li key={link.href + link.label}>
                  <Link to={link.href} className="text-muted-foreground hover:text-foreground text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-4 text-base">{t.footer.resources}</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.href + link.label}>
                  <Link to={link.href} className="text-muted-foreground hover:text-foreground text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-4 text-base">{t.footer.company}</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.href + link.label}>
                  <Link to={link.href} className="text-muted-foreground hover:text-foreground text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* BTC Donation Banner */}
        <div className="mt-8 p-4 rounded-lg bg-primary/5 border border-primary/20">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-sm font-medium text-foreground">{t.footer.supportTitle}</p>
                <p className="text-xs text-muted-foreground">{t.footer.supportDesc}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono hidden sm:block">
                {BTC_ADDRESS.slice(0, 8)}...{BTC_ADDRESS.slice(-6)}
              </code>
              <Button variant="outline" size="sm" onClick={copyBtc} className="gap-2">
                <Copy className={`w-3 h-3 ${copied ? 'text-primary' : ''}`} />
                {copied ? t.footer.copied : t.footer.copyBTC}
              </Button>
              <Link to="/support">
                <Button variant="ghost" size="sm">
                  {t.footer.learnMore}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            {t.footer.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}
